/**
 * The tab's Keycloak tokens, kept in `sessionStorage` so a reload (or a
 * new page load in the same tab) resumes the session with one token
 * refresh instead of a redirect round-trip through Keycloak and a
 * second full load of the app. From Japan to the server that redirect
 * costs about three round trips plus a second app start.
 *
 * `sessionStorage` (not `localStorage`): per tab, gone when the tab
 * closes. A stale or revoked refresh token just fails the first refresh
 * and falls back to the normal sign-in redirect.
 */

const KEY = 'relay.kc';

export type StoredTokens = { token: string; refreshToken: string; idToken?: string };

export function loadTokens(): StoredTokens | null {
  try {
    const raw = window.sessionStorage.getItem(KEY);
    if (!raw) return null;
    const t = JSON.parse(raw) as Partial<StoredTokens>;
    return t.token && t.refreshToken ? (t as StoredTokens) : null;
  } catch {
    return null;
  }
}

export function saveTokens(t: { token?: string; refreshToken?: string; idToken?: string }): void {
  if (!t.token || !t.refreshToken) return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify({ token: t.token, refreshToken: t.refreshToken, idToken: t.idToken }));
  } catch {
    /* storage blocked: the app still works, it just redirects on reload */
  }
}

export function clearTokens(): void {
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
